import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, Pencil, UserX, Trash2, Search, X } from "lucide-react";
import { Modal, Field } from "@/pages/ClientsPage";

const empty = { name: "", email: "", password: "", role: "empleado", phone: "", client_id: "", location_id: "" };
const ROLES = [
  { v: "admin", l: "Administrador" },
  { v: "supervisor", l: "Supervisor" },
  { v: "empleado", l: "Empleado de limpieza" },
  { v: "cliente", l: "Cliente" },
];

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  // Filters
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const load = async () => {
    setLoading(true);
    const [u, c, l] = await Promise.all([api.get("/users"), api.get("/clients"), api.get("/locations")]);
    setItems(u.data); setClients(c.data); setLocations(l.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const cliMap = useMemo(() => Object.fromEntries(clients.map(c=>[c.id, c.name])), [clients]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(u => {
      if (filterRole && u.role !== filterRole) return false;
      if (filterClient === "__none" && u.client_id) return false;
      if (filterClient && filterClient !== "__none" && u.client_id !== filterClient) return false;
      if (filterStatus && (u.status || "active") !== filterStatus) return false;
      if (q && !(u.name || "").toLowerCase().includes(q) && !(u.email || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, filterRole, filterClient, filterStatus]);

  const hasActiveFilters = search || filterRole || filterClient || filterStatus;
  const clearFilters = () => { setSearch(""); setFilterRole(""); setFilterClient(""); setFilterStatus(""); };

  const openNew = () => { setEditing(null); setForm(empty); setErr(""); setModalOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it, password: "" }); setErr(""); setModalOpen(true); };
  const close = () => setModalOpen(false);

  const save = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password;
      // remove email when editing (not allowed to change)
      if (editing) delete payload.email;
      if (editing) await api.put(`/users/${editing.id}`, payload);
      else await api.post("/users", payload);
      close(); await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const deactivate = async (id) => {
    if (!window.confirm("¿Desactivar usuario?\n\nNo podrá iniciar sesión pero su historial se conserva.")) return;
    await api.delete(`/users/${id}`); await load();
  };

  const removePermanent = async (u) => {
    const msg = `¿ELIMINAR PERMANENTEMENTE a "${u.name}"?\n\nEsta acción NO se puede deshacer.\nEl historial de check-ins se conserva pero el usuario quedará como "—".\n\nEscribe "ELIMINAR" para confirmar:`;
    const confirm = window.prompt(msg);
    if (confirm !== "ELIMINAR") return;
    try {
      await api.delete(`/users/${u.id}/permanent`);
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="lx-overline">Equipo</div>
          <h1 className="lx-section-title mt-1">Usuarios</h1>
          <p className="text-lx-muted text-sm mt-1">Gestiona los miembros del equipo y sus permisos.</p>
        </div>
        <button className="lx-btn lx-btn-primary" onClick={openNew} data-testid="new-user-button">
          <Plus size={16}/> Nuevo usuario
        </button>
      </div>

      <div className="lx-card space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-1">
            <label className="lx-label">Buscar</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-lx-muted"/>
              <input
                type="search"
                className="lx-input pl-9"
                placeholder="Nombre o email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="users-search-input"
              />
            </div>
          </div>
          <div>
            <label className="lx-label">Rol</label>
            <select className="lx-input" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} data-testid="users-filter-role">
              <option value="">Todos</option>
              {ROLES.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          <div>
            <label className="lx-label">Cliente</label>
            <select className="lx-input" value={filterClient} onChange={(e) => setFilterClient(e.target.value)} data-testid="users-filter-client">
              <option value="">Todos</option>
              <option value="__none">Sin cliente asignado</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="lx-label">Estatus</label>
            <select className="lx-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="users-filter-status">
              <option value="">Todos</option>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-lx-muted">
          <span data-testid="users-count">
            Mostrando <strong className="text-lx-text">{filtered.length}</strong> de {items.length} usuarios
          </span>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="inline-flex items-center gap-1 text-lx-primary font-semibold hover:underline" data-testid="users-clear-filters">
              <X size={12}/> Limpiar filtros
            </button>
          )}
        </div>
      </div>

      <div className="lx-card p-0 overflow-hidden">
        {loading ? <div className="p-6 text-lx-muted">Cargando…</div> : filtered.length === 0 ? (
          <div className="p-10 text-center text-lx-muted">
            {hasActiveFilters ? "Sin resultados con los filtros aplicados." : "Sin usuarios."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="lx-table">
              <thead><tr><th>Nombre</th><th>Email</th><th>Rol</th><th>Cliente</th><th>Estatus</th><th className="text-right">Acciones</th></tr></thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="font-medium">{u.name}</td>
                    <td>{u.email}</td>
                    <td><span className="lx-badge lx-badge-info">{ROLES.find(r=>r.v===u.role)?.l || u.role}</span></td>
                    <td>{cliMap[u.client_id] || "—"}</td>
                    <td>
                      <span className={`lx-badge ${u.status === "active" ? "lx-badge-success" : "lx-badge-muted"}`}>
                        {u.status === "active" ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button className="lx-btn lx-btn-ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} title="Editar"><Pencil size={14}/></button>
                      <button className="lx-btn lx-btn-ghost" onClick={() => deactivate(u.id)} data-testid={`deactivate-user-${u.id}`} title="Desactivar (conserva historial)"><UserX size={14}/></button>
                      <button className="lx-btn lx-btn-ghost text-red-600 hover:bg-red-50" onClick={() => removePermanent(u)} data-testid={`delete-user-${u.id}`} title="Eliminar permanentemente"><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <Modal title={editing ? "Editar usuario" : "Nuevo usuario"} onClose={close}>
          <form onSubmit={save} className="space-y-4">
            <Field label="Nombre" required value={form.name} onChange={(v)=>setForm({...form, name: v})} testid="user-name"/>
            <Field label="Email" type="email" required={!editing} value={form.email} onChange={(v)=>setForm({...form, email: v})} testid="user-email"/>
            <Field label={editing ? "Nueva contraseña (opcional)" : "Contraseña"} type="password" required={!editing} value={form.password} onChange={(v)=>setForm({...form, password: v})} testid="user-password"/>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="lx-label">Rol</label>
                <select className="lx-input" value={form.role} onChange={(e)=>setForm({...form, role: e.target.value})} data-testid="user-role">
                  {ROLES.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
                </select>
              </div>
              <Field label="Teléfono" value={form.phone} onChange={(v)=>setForm({...form, phone: v})} testid="user-phone"/>
            </div>
            {(form.role === "cliente" || form.role === "empleado" || form.role === "supervisor") && (
              <div>
                <label className="lx-label">Cliente asociado {form.role === "cliente" && <span className="text-lx-error">*</span>}</label>
                <select className="lx-input" value={form.client_id || ""} onChange={(e)=>setForm({...form, client_id: e.target.value})} data-testid="user-client">
                  <option value="">— Ninguno —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {err && <div className="text-sm rounded-lg px-3 py-2 bg-red-50 text-red-700 border border-red-200">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="lx-btn lx-btn-secondary" onClick={close}>Cancelar</button>
              <button type="submit" className="lx-btn lx-btn-primary" disabled={saving} data-testid="save-user-button">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
